import { Header } from '@components/Header';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useAuthContext } from '@providers/AuthProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { routes } from '@routes';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { apiKeysKeys } from 'app/queries/apiKeys/apiKeysKeys';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';
import { EPermission } from 'app/types/enums/EPermission';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiKeysList } from './ApiKeysList';
import { BillingAccountList } from './BillingAccountList';
import { NameSection } from './NameSection';
import { NameSectionViewOnly } from './NameSection/NameSectionViewOnly';
import { RepositoriesTable } from './RepositoriesTable';
import { Content } from './styles/Content';
import { TeamsList } from './TeamsList';
import { WorkspaceTable } from './WorkspaceTable';

export const OrganizationDetails = () => {
  const { me, organization, isOrgResolved } = useAuthContext();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();

  const organizationId = organization?.id ?? '';
  const fetchingSectionsCount =
    useIsFetching({ queryKey: workspacesKeys.listsByOrganization(organizationId) }) +
    useIsFetching({ queryKey: repositoriesKeys.listsByOrg(organizationId) }) +
    useIsFetching({ queryKey: teamsKeys.listsByOrganization(organizationId) }) +
    useIsFetching({ queryKey: apiKeysKeys.list(organizationId) });
  const savingOrganizationCount = useIsMutating({ mutationKey: organizationsKeys.detail(organizationId) });
  const isLoading = fetchingSectionsCount + savingOrganizationCount > 0;

  useEffect(() => {
    if (!isOrgResolved) {
      return;
    }
    if (organization && organization.slug === orgSlug) {
      return;
    }
    const fallbackOrgSlug = organization?.slug ?? me.organizations?.[0]?.slug;
    if (!fallbackOrgSlug) {
      return;
    }
    navigate(routes.dashboard.make(fallbackOrgSlug), { replace: true });
  }, [isOrgResolved, organization, orgSlug, navigate, me.organizations]);

  if (!isOrgResolved || !organization || organization.slug !== orgSlug) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{organization.name} - AutoScript</title>
      </Helmet>
      <Header title={organization.name} />

      <LinearProgressLoader active={isLoading} />
      <Content>
        <div>
          <Restricted
            to={EPermission.manageOrganization}
            fallback={<NameSectionViewOnly organization={organization} />}
          >
            <NameSection />
          </Restricted>

          <Restricted to={EPermission.manageWorkspaces}>
            <WorkspaceTable />
          </Restricted>

          <Restricted to={EPermission.viewRepositories}>
            <RepositoriesTable />
          </Restricted>

          <Restricted to={EPermission.manageMembers}>
            <TeamsList />
          </Restricted>

          <Restricted to={EPermission.viewBilling}>
            <BillingAccountList />
          </Restricted>

          <Restricted to={EPermission.manageOrganization}>
            <ApiKeysList />
            <div className="mt-1">
              <Link
                to={routes.organizationApiDocs.make()}
                className="text-xs text-[var(--color-text-subtle)] hover:text-[var(--color-title)] hover:underline"
              >
                View API documentation →
              </Link>
            </div>
          </Restricted>
        </div>
      </Content>
    </>
  );
};
